import { useCallback, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { TextInput } from "../../components-redesign/TextInput/TextInput";
import { useLocalTranslationState } from "../../context/AppContext/AppContext";
import { styled } from "@mui/material/styles";
import { StatusListItem, StatusListItemState } from "./StatusListItem";
import {
  PASSWORD_REQUIREMENTS
} from '../../constants/global';
import { isMinLength, isMaxLength, containsNumber, containsLowercase, containsUppercase } from "../../utilities/validations";

const passwordLengthMinimum = PASSWORD_REQUIREMENTS.minLength
const passwordLengthMaximum = PASSWORD_REQUIREMENTS.maxLength

const TextContainer = styled("div")({
  alignSelf: "stretch",
});

interface NewPasswordProps {
  password: string;
  setPassword: (password) => void;
  setIsValid: (valid) => void;
  label?: string;
  setError: (error) => void;
  error: string;
  overrideAutoComplete?: boolean;
}

const NewPassword = ({
  password,
  setPassword,
  setIsValid,
  label,
  error,
  setError,
  overrideAutoComplete,
}: NewPasswordProps) => {

  const { _translate } = useLocalTranslationState();

  const [passwordError, setPasswordError] = useState("");
  const [charactersLongError, setCharactersLongError] = useState(StatusListItemState.Unchecked);
  const [uppercaseError, setUppercaseError] = useState(StatusListItemState.Unchecked);
  const [lowercaseError, setLowercaseError] = useState(StatusListItemState.Unchecked);
  const [numberError, setNumberError] = useState(StatusListItemState.Unchecked);

  const validatePassword = useCallback((password: string) => {
    if (!password) {
      setCharactersLongError(StatusListItemState.Unchecked);
      setLowercaseError(StatusListItemState.Unchecked);
      setUppercaseError(StatusListItemState.Unchecked);
      setNumberError(StatusListItemState.Unchecked);
      setPasswordError(_translate("Error, please correct this field"));
      setIsValid(false);
      return;
    }

    const isLengthValid = isMinLength(password, passwordLengthMinimum) && isMaxLength(password, passwordLengthMaximum);
    const hasLower = containsLowercase(password);
    const hasUpper = containsUppercase(password);
    const hasNum = containsNumber(password);

    setCharactersLongError(isLengthValid ? StatusListItemState.Valid : StatusListItemState.Error);
    setLowercaseError(hasLower ? StatusListItemState.Valid : StatusListItemState.Error);
    setUppercaseError(hasUpper ? StatusListItemState.Valid : StatusListItemState.Error);
    setNumberError(hasNum ? StatusListItemState.Valid : StatusListItemState.Error);

    const isValid = isLengthValid && hasLower && hasUpper && hasNum;

    if (isValid) {
      setPasswordError("");
      setError("");
    } else {
      setPasswordError(_translate("Error, please correct this field"));
    }

    setIsValid(isValid);
  }, []);

  return (
    <>
      <TextInput
        fieldName="password"
        id="password"
        dataTestId="password"
        label={label ?? _translate("Password")}
        type={'password'}
        value={password}
        isDisabled={false}
        isRequired={true}
        errorInPlainText={false}
        overrideAutoComplete={overrideAutoComplete}
        error={passwordError || error}
        onClear={() => {
          setPassword('');
          validatePassword('');
        }}
        onChange={(event) => {
          const value = event.target.value;
          setPassword(value);
          validatePassword(value);
        }}
      />
      <TextContainer>
        <Typography variant="subtitle1" fontWeight="600"
          sx={(theme) => ({
            paddingBottom: theme.spacing(.5),
          })}>
          {_translate("Your password must")}
        </Typography>
      </TextContainer>
      <Grid container
        sx={{
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}>
        <Grid>
          <StatusListItem error={charactersLongError} text={_translate(`Be 8 to 256 characters long`)} />
        </Grid>
        <Grid>
          <StatusListItem error={uppercaseError} text={_translate("Contain an uppercase letter")} />
        </Grid>
        <Grid>
          <StatusListItem error={lowercaseError} text={_translate("Contain a lowercase letter")} />
        </Grid>
        <Grid>
          <StatusListItem error={numberError} text={_translate("Contain a number")} />
        </Grid>
      </Grid>
    </>
  );
};

export default NewPassword;